import { ChevronDown, Loader2, X, Check } from "lucide-react";
import DeleteAccountModal from "./delete-account-modal";
import React, { useRef, useState, useEffect } from "react";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import tarrantCountyZipCodes from "../../data/tarrant-county-zip-codes.json";
import {
  ALLOWED_PROFILE_PICTURE_TYPES,
  ALLOWED_REGISTRATION_DOCUMENT_TYPES,
  validateFileSize,
  validateFileType,
} from "../../config/fileConfig";
import toast from "react-hot-toast";


export default function MainUserSetting() {
  const [, setFileName] = useState("No file selected");
  const [preview, setPreview] = useState("/assets/images/user-avatar.png"); // default image
  const fileInputRefProfile = useRef(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [registrationDocuments, setRegistrationDocuments] = useState([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  // Services state
  const [servicesOptions, setServicesOptions] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const servicesDropdownRef = useRef(null);

  // Zip code state
  const [zipCodeSearch, setZipCodeSearch] = useState("");
  const [isZipCodeDropdownOpen, setIsZipCodeDropdownOpen] = useState(false);
  const zipCodeDropdownRef = useRef(null);

  const INCOME_RANGES = [
    "$0 - $500",
    "$500 - $1,000",
    "$1,000 - $2,000",
    "$2,000 - $3,500",
    "$3,500 - $5,000",
    "$5,000+",
  ];

  const [isIncomeDropdownOpen, setIsIncomeDropdownOpen] = useState(false);
  const incomeDropdownRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    organizationName: "",
    ein: "",
    address: "",
    zipCode: "",
    email: "",
    phoneNumber: "",
    registrationDocuments: "",
    mission: "",
    hoursOfOperation: "",
    incomeRequirement: "",
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [notificationPrefs, setNotificationPrefs] = useState({
    announcements: true,
    collaborationRequests: true,
  });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const typeValidation = validateFileType(
      file,
      ALLOWED_PROFILE_PICTURE_TYPES,
    );
    if (!typeValidation.valid) {
      toast.error(typeValidation.error);
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.valid) {
      toast.error(sizeValidation.error);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to backend
    setIsUploadingProfile(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Please login to upload profile picture");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", "PROFILE_PICTURE");

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.UPLOAD_USER_DOCUMENT}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (response.ok) {
        const documentData = result.data || result;
        setProfilePictureUrl(documentData.documentLink);
        toast.success("Profile picture uploaded successfully!");
      } else {
        const errorMessage =
          result.message || "Failed to upload profile picture";
        toast.error(errorMessage);
        // Revert preview on error
        setPreview(profilePictureUrl || "/assets/images/user.jpg");
      }
    } catch (err) {
      console.error("Error uploading profile picture:", err);
      toast.error("Failed to upload profile picture. Please try again.");
      // Revert preview on error
      setPreview(profilePictureUrl || "/assets/images/user.jpg");
    } finally {
      setIsUploadingProfile(false);
    }
  };

  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (only Word files and PDFs for registration documents)
    const typeValidation = validateFileType(
      file,
      ALLOWED_REGISTRATION_DOCUMENT_TYPES,
    );
    if (!typeValidation.valid) {
      toast.error(typeValidation.error);
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.valid) {
      toast.error(sizeValidation.error);
      return;
    }

    setFileName(file.name);

    // Upload to backend
    setIsUploadingDocument(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Please login to upload document");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", "REGISTRATION_DOCUMENT");

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.UPLOAD_USER_DOCUMENT}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (response.ok) {
        // Refresh documents list
        await fetchUserDocuments();
        toast.success("Registration document uploaded successfully!");
      } else {
        const errorMessage = result.message || "Failed to upload document";
        toast.error(errorMessage);
        setFileName("No file selected");
      }
    } catch (err) {
      console.error("Error uploading document:", err);
      toast.error("Failed to upload document. Please try again.");
      setFileName("No file selected");
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleTextClick = () => {
    fileInputRef.current?.click();
  };

  const fetchUserDocuments = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      // Fetch profile picture
      const profileResponse = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_USER_DOCUMENTS}?documentType=PROFILE_PICTURE`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (profileResponse.ok) {
        const profileResult = await profileResponse.json();
        const profileData = profileResult.data || profileResult;
        if (Array.isArray(profileData) && profileData.length > 0) {
          const profilePic = profileData[0];
          setProfilePictureUrl(profilePic.documentLink);
          setPreview(profilePic.documentLink);
        }
      }

      // Fetch registration documents
      const docsResponse = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_USER_DOCUMENTS}?documentType=REGISTRATION_DOCUMENT`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (docsResponse.ok) {
        const docsResult = await docsResponse.json();
        const docsData = docsResult.data || docsResult;
        if (Array.isArray(docsData)) {
          setRegistrationDocuments(docsData);
          if (docsData.length > 0) {
            setFileName(
              docsData[0].filename ||
                docsData[0].documentLink.split("/").pop() ||
                "Registration document",
            );
          }
        }
      }
    } catch (err) {
      console.error("Error fetching user documents:", err);
    }
  };

  // Fetch services LOVs on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchServices = async () => {
      setIsLoadingServices(true);
      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_LOVS_PUBLIC}?key=NONPROFIT_SERVICES`,
        );
        if (response.ok) {
          const result = await response.json();
          const services = result?.data || result;
          if (isMounted) {
            setServicesOptions(Array.isArray(services) ? services : []);
          }
        } else {
          console.error("Failed to fetch services");
          if (isMounted) {
            toast.error("Failed to load services. Please refresh the page.");
          }
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        if (isMounted) {
          toast.error("Failed to load services. Please refresh the page.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingServices(false);
        }
      }
    };

    fetchServices();

    return () => {
      isMounted = false;
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target)
      ) {
        setIsServicesDropdownOpen(false);
      }
      if (
        zipCodeDropdownRef.current &&
        !zipCodeDropdownRef.current.contains(event.target)
      ) {
        setIsZipCodeDropdownOpen(false);
      }
      if (
        incomeDropdownRef.current &&
        !incomeDropdownRef.current.contains(event.target)
      ) {
        setIsIncomeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch current user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setFetching(true);
        const token = localStorage.getItem("accessToken");
        if (!token) {
          toast.error("Please login to view your profile");
          setFetching(false);
          return;
        }

        // Try nonprofits/profile/me first, fallback to users/me if needed
        let response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_PROFILE}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        let result = await response.json();
        console.log("Profile API response:", result);

        // If nonprofits endpoint fails, try users/me as fallback
        if (!response.ok) {
          console.log("Nonprofits endpoint failed, trying users/me...");
          response = await fetch(`${BASE_URL}${API_ENDPOINTS.USERS_ME}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          result = await response.json();
          console.log("Users/me API response:", result);
        }

        if (response.ok && result) {
          // Extract data from response wrapper (BaseResponseDto structure)
          // Response structure: { statusCode, data, message, timestamp }
          const responseData = result.data || result;
          console.log("Extracted response data:", responseData);

          // Handle two possible response structures:
          // 1. From /nonprofits/profile/me: data at root level with contact object
          // 2. From /users/me: data nested in nonProfitUser object
          let nonprofitData = null;
          let email = "";
          let services = [];

          if (responseData.nonProfitUser) {
            // Response from /users/me
            nonprofitData = responseData.nonProfitUser;
            email = responseData.email || "";
            // Extract services from nested structure if available
            if (responseData.nonProfitUser?.services) {
              services = responseData.nonProfitUser.services;
            }
            console.log(
              "Using /users/me structure, nonprofitData:",
              nonprofitData,
            );
          } else if (responseData.organizationName || responseData.contact) {
            // Response from /nonprofits/profile/me
            nonprofitData = responseData;
            email = responseData.contact?.email || responseData.email || "";
            // Extract services from response
            if (responseData.services) {
              services = responseData.services;
            }
            console.log(
              "Using /nonprofits/profile/me structure, nonprofitData:",
              nonprofitData,
            );
          }

          if (nonprofitData) {
            // Populate form with existing data
            setFormData({
              organizationName: nonprofitData.organizationName || "",
              ein: nonprofitData.ein || "",
              address: nonprofitData.address || "",
              zipCode: nonprofitData.zipCode || "",
              email: email,
              phoneNumber: nonprofitData.phoneNumber || "",
              registrationDocuments: nonprofitData.registrationDocuments || "",
              mission: nonprofitData.mission || "",
              hoursOfOperation: nonprofitData.hoursOfOperation || "",
              incomeRequirement: nonprofitData.incomeRequirement || "",
            });
            setZipCodeSearch(nonprofitData.zipCode || "");

            // Set notification preferences from saved profile
            if (nonprofitData.preferences) {
              setNotificationPrefs({
                announcements: nonprofitData.preferences.announcements !== false,
                collaborationRequests: nonprofitData.preferences.collaborationRequests !== false,
              });
            }

            // Set selected services
            if (Array.isArray(services) && services.length > 0) {
              const serviceIds = services.map((service) => service.id);
              setSelectedServices(serviceIds);
            }

            console.log("Form data populated successfully");
          } else {
            console.error(
              "No nonprofit data found. Response structure:",
              responseData,
            );
            toast.error(
              "No nonprofit profile data found. Please ensure you have a nonprofit profile.",
            );
          }
        } else {
          const errorMessage =
            result.message ||
            (Array.isArray(result.message)
              ? result.message.join(", ")
              : "Failed to fetch profile");
          toast.error(errorMessage);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        toast.error("Failed to load profile. Please try again.");
      } finally {
        setFetching(false);
      }
    };

    fetchProfile();
    fetchUserDocuments();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrefToggle = async (key) => {
    const updated = { ...notificationPrefs, [key]: !notificationPrefs[key] };
    setNotificationPrefs(updated);
    try {
      const token = localStorage.getItem("accessToken");
      await fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_UPDATE_PROFILE}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ preferences: updated }),
      });
    } catch {
      // revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error("Failed to save preference.");
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Please login to update your profile");
        setLoading(false);
        return;
      }

      const updateData = {
        organizationName: formData.organizationName,
        ein: formData.ein || undefined,
        address: formData.address || undefined,
        zipCode: formData.zipCode || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        registrationDocuments: formData.registrationDocuments || undefined,
        mission: formData.mission || undefined,
        hoursOfOperation: formData.hoursOfOperation || undefined,
        incomeRequirement: formData.incomeRequirement || undefined,
        serviceIds: selectedServices.length > 0 ? selectedServices : undefined,
      };

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_UPDATE_PROFILE}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        },
      );

      const result = await response.json();

      // Show toast based on response status code
      if (response.ok) {
        const statusCode = result.statusCode || response.status;
        if (statusCode === 200 || statusCode === 201) {
          toast.success(result.message || "Profile updated successfully!");
        } else {
          toast.success(result.message || "Profile updated successfully!");
        }

        // Update form data with response
        const responseData = result.data || result;
        if (responseData) {
          setFormData((prev) => ({
            ...prev,
            organizationName:
              responseData.organizationName || prev.organizationName,
            ein: responseData.ein || prev.ein,
            address: responseData.address || prev.address,
            zipCode: responseData.zipCode || prev.zipCode,
            phoneNumber: responseData.phoneNumber || prev.phoneNumber,
            registrationDocuments:
              responseData.registrationDocuments || prev.registrationDocuments,
          }));
          if (responseData.zipCode) {
            setZipCodeSearch(responseData.zipCode);
          }

          // Update selected services if available in response
          if (responseData.services && Array.isArray(responseData.services)) {
            const serviceIds = responseData.services.map(
              (service) => service.id,
            );
            setSelectedServices(serviceIds);
          }
        }
      } else {
        const statusCode = result.statusCode || response.status;
        const errorMessage =
          result.message ||
          (Array.isArray(result.message)
            ? result.message.join(", ")
            : "Failed to update profile");

        if (statusCode === 400) {
          toast.error(
            errorMessage || "Invalid request. Please check your input.",
          );
        } else if (statusCode === 401) {
          toast.error("Unauthorized. Please login again.");
        } else if (statusCode === 403) {
          toast.error("You don't have permission to perform this action.");
        } else if (statusCode === 404) {
          toast.error("Profile not found.");
        } else if (statusCode >= 500) {
          toast.error("Server error. Please try again later.");
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col-reverse lg:flex-row lg:justify-between mb-5">
      <div className="w-full lg:w-[70%] mt-8 lg:mt-0">
        <div>
          <h1 className="font-popinsBold text-xl lg:text-2xl">User Settings</h1>
          <p className="font-popinsRegular text-sm text-black/50 mt-2">
            Manage your account preferences and system settings.
          </p>
        </div>
        <form className="lg:w-[60%] mt-8" onSubmit={handleSubmit}>
          <h3 className="font-popinsSemiBold text-lg">Profile Settings</h3>

          {fetching ? (
            <div className="mt-5 text-center py-8">
              <p className="font-popinsRegular text-sm text-black/50">
                Loading profile...
              </p>
            </div>
          ) : (
            <>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">
                  Organization Name
                </label>
                <input
                  type="text"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  placeholder="Green Leave"
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  required
                />
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">
                  EIN (Tax ID)
                </label>
                <input
                  type="text"
                  name="ein"
                  value={formData.ein}
                  onChange={handleInputChange}
                  placeholder="EIN (Tax ID)"
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                />
              </div>
              <div className="mt-5 flex flex-col lg:flex-row lg:justify-between">
                <div className="w-full lg:w-[49%]">
                  <label className="font-popinsMedium text-sm">
                    Address (Street, City, State)
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Address (Street, City, State)"
                    className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  />
                </div>
                <div className="relative w-full lg:w-[49%] mt-3 lg:mt-0" ref={zipCodeDropdownRef}>
                  <label className="font-popinsMedium text-sm">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    placeholder="ZIP Code"
                    value={zipCodeSearch}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setZipCodeSearch(val);
                      setFormData((prev) => ({ ...prev, zipCode: "" }));
                      setIsZipCodeDropdownOpen(true);
                    }}
                    onFocus={() => setIsZipCodeDropdownOpen(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        if (!formData.zipCode) {
                          setZipCodeSearch("");
                        }
                      }, 200);
                    }}
                    className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 pr-10 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  />
                  <ChevronDown
                    size={20}
                    className={`text-black/50 transition-transform absolute right-3 bottom-3 pointer-events-none ${
                      isZipCodeDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                  {isZipCodeDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {(() => {
                        const filtered = tarrantCountyZipCodes.filter((z) =>
                          z.startsWith(zipCodeSearch)
                        );
                        if (filtered.length === 0) {
                          return (
                            <div className="px-4 py-2 text-sm text-black/50 font-popinsMedium">
                              No Match Found
                            </div>
                          );
                        }
                        return filtered.map((z) => (
                          <div
                            key={z}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, zipCode: z }));
                              setZipCodeSearch(z);
                              setIsZipCodeDropdownOpen(false);
                            }}
                            className="px-4 py-2 text-sm font-popinsMedium text-black/50 cursor-pointer hover:bg-[#3FC4E3]/10"
                          >
                            {z}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-gray-100 px-4 py-2 text-black/50 outline-none cursor-not-allowed"
                  disabled
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed
                </p>
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">Phone no</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="09121212121"
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                />
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">Mission Statement</label>
                <textarea
                  name="mission"
                  value={formData.mission}
                  onChange={handleInputChange}
                  placeholder="Describe your organization's mission and goals…"
                  rows={4}
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] resize-none"
                />
              </div>
              <div className="mt-5">
                <label className="font-popinsMedium text-sm">Hours of Operation</label>
                <input
                  type="text"
                  name="hoursOfOperation"
                  value={formData.hoursOfOperation}
                  onChange={handleInputChange}
                  placeholder="e.g. Mon–Fri 9am–5pm"
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                />
              </div>
              <div className="mt-5 relative" ref={incomeDropdownRef}>
                <label className="font-popinsMedium text-sm">Income Requirements</label>
                <button
                  type="button"
                  onClick={() => setIsIncomeDropdownOpen(!isIncomeDropdownOpen)}
                  className="w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 pr-10 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] text-left relative flex items-center"
                >
                  <span className={formData.incomeRequirement ? "text-black/50" : "text-gray-400"}>
                    {formData.incomeRequirement || "Select income range"}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isIncomeDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isIncomeDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg">
                    <div
                      onClick={() => { setFormData((prev) => ({ ...prev, incomeRequirement: "" })); setIsIncomeDropdownOpen(false); }}
                      className="px-4 py-2 text-sm font-popinsMedium text-gray-400 cursor-pointer hover:bg-[#3FC4E3]/10"
                    >
                      None
                    </div>
                    {INCOME_RANGES.map((range) => (
                      <div
                        key={range}
                        onClick={() => { setFormData((prev) => ({ ...prev, incomeRequirement: range })); setIsIncomeDropdownOpen(false); }}
                        className={`px-4 py-2 text-sm font-popinsMedium cursor-pointer hover:bg-[#3FC4E3]/10 flex items-center justify-between ${formData.incomeRequirement === range ? "text-[#3FC4E3]" : "text-black/50"}`}
                      >
                        {range}
                        {formData.incomeRequirement === range && <Check size={14} className="text-[#3FC4E3]" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-5 relative" ref={servicesDropdownRef}>
                <label className="font-popinsMedium text-sm">
                  Services Offered
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setIsServicesDropdownOpen(!isServicesDropdownOpen)
                  }
                  className={`w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white pl-3 pr-10 py-1 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] appearance-none cursor-pointer text-left h-[42px] flex items-center relative ${
                    selectedServices.length === 0
                      ? "text-gray-400"
                      : "text-black/50"
                  }`}
                  disabled={isLoadingServices}
                >
                  <div
                    className={`flex-1 flex flex-nowrap gap-1.5 items-center min-w-0 pr-3 pb-1 ${
                      selectedServices.length > 0
                        ? "overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                        : "overflow-hidden"
                    }`}
                  >
                    {isLoadingServices ? (
                      <span className="text-gray-400 whitespace-nowrap">
                        Loading services...
                      </span>
                    ) : selectedServices.length === 0 ? (
                      <span className="text-gray-400 whitespace-nowrap">
                        Select services offered
                      </span>
                    ) : (
                      selectedServices.map((serviceId) => {
                        const service = servicesOptions.find(
                          (s) => s.id === serviceId,
                        );
                        if (!service) return null;
                        return (
                          <span
                            key={serviceId}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-md text-xs font-popinsMedium whitespace-nowrap flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedServices(
                                selectedServices.filter(
                                  (id) => id !== serviceId,
                                ),
                              );
                            }}
                          >
                            <span>{service.name}</span>
                            <X
                              size={12}
                              className="cursor-pointer hover:text-[#006077] transition-colors flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedServices(
                                  selectedServices.filter(
                                    (id) => id !== serviceId,
                                  ),
                                );
                              }}
                            />
                          </span>
                        );
                      })
                    )}
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                      isServicesDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isServicesDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {servicesOptions.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        No services available
                      </div>
                    ) : (
                      servicesOptions.map((service) => {
                        const isSelected = selectedServices.includes(
                          service.id,
                        );
                        return (
                          <div
                            key={service.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedServices(
                                  selectedServices.filter(
                                    (id) => id !== service.id,
                                  ),
                                );
                              } else {
                                setSelectedServices([
                                  ...selectedServices,
                                  service.id,
                                ]);
                              }
                            }}
                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                          >
                            <div
                              className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                                isSelected
                                  ? "border-[#3FC4E3] bg-[#3FC4E3]"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <Check size={14} className="text-white" />
                              )}
                            </div>
                            <span className="font-popinsRegular text-sm text-black/50">
                              {service.name}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          <div className="mt-5">
            <label className="font-popinsMedium text-sm">
              Registration Documents
            </label>
            <div className="mt-2">
              {registrationDocuments.length > 0 ? (
                <div className="space-y-2">
                  {registrationDocuments.map((doc) => {
                    const handleDownload = async (e) => {
                      e.preventDefault();
                      try {
                        const response = await fetch(doc.documentLink);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = doc.filename || "document";
                        if (doc.mimeType) {
                          link.type = doc.mimeType;
                        }
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error("Error downloading file:", error);
                        toast.error(
                          "Failed to download file. Please try again.",
                        );
                      }
                    };

                    const handleDelete = async (e) => {
                      e.preventDefault();
                      if (
                        !window.confirm(
                          "Are you sure you want to delete this document?",
                        )
                      ) {
                        return;
                      }

                      setDeletingDocumentId(doc.id);
                      try {
                        const token = localStorage.getItem("accessToken");
                        if (!token) {
                          toast.error("Please login to delete document");
                          return;
                        }

                        const response = await fetch(
                          `${BASE_URL}${API_ENDPOINTS.DELETE_USER_DOCUMENT}/${doc.id}`,
                          {
                            method: "DELETE",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`,
                            },
                          },
                        );

                        const result = await response.json();

                        if (response.ok) {
                          toast.success("Document deleted successfully!");
                          // Refresh documents list
                          await fetchUserDocuments();
                        } else {
                          const errorMessage =
                            result.message || "Failed to delete document";
                          toast.error(errorMessage);
                        }
                      } catch (err) {
                        console.error("Error deleting document:", err);
                        toast.error(
                          "Failed to delete document. Please try again.",
                        );
                      } finally {
                        setDeletingDocumentId(null);
                      }
                    };

                    return (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                      >
                        <button
                          onClick={handleDownload}
                          className="font-popinsRegular text-sm text-[#3FC4E3] cursor-pointer underline text-left hover:text-[#2da3c0] transition-colors flex-1"
                        >
                          {doc.filename ||
                            doc.documentLink.split("/").pop() ||
                            "Document"}
                        </button>
                        <button
                          onClick={handleDelete}
                          disabled={deletingDocumentId === doc.id}
                          className="ml-3 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Delete document"
                        >
                          {deletingDocumentId === doc.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="font-popinsRegular text-sm text-gray-500">
                  No documents uploaded
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleTextClick}
              disabled={isUploadingDocument}
              className="font-popinsRegular text-sm text-[#3FC4E3] cursor-pointer underline mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploadingDocument ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                "Upload New Document"
              )}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx"
              disabled={isUploadingDocument}
            />
          </div>
          {!fetching && (
            <div>
              <button
                type="submit"
                disabled={loading}
                className="relative overflow-hidden py-2 px-6 bg-themeColor text-white font-popinsSemiBold text-base mt-8 rounded-md shadow-lg
             transform transition duration-300 ease-in-out
             hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
             active:scale-95 active:shadow-inner focus:outline-none
             disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg flex items-center gap-2 justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  "Update Profile"
                )}
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
            </div>
          )}
        </form>
        <div className="mt-8">
          <h3 className="font-popinsSemiBold">Notification Preferences</h3>
          <div className="flex justify-between my-5">
            <p className="font-popinsRegular text-sm">Announcements</p>
            <label className="inline-flex items-center cursor-pointer relative pl-3">
              <input type="checkbox" className="sr-only peer" checked={notificationPrefs.announcements} onChange={() => handlePrefToggle("announcements")} />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-2 after:border-themeColor after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-themeColor"></div>
            </label>
          </div>
          <div className="flex justify-between">
            <p className="font-popinsRegular text-sm">Collaboration Requests</p>
            <label className="inline-flex items-center cursor-pointer relative pl-3">
              <input type="checkbox" className="sr-only peer" checked={notificationPrefs.collaborationRequests} onChange={() => handlePrefToggle("collaborationRequests")} />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-2 after:border-themeColor after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-themeColor"></div>
            </label>
          </div>
        </div>
        <div className="mt-6">
          <p className="font-popinsSemiBold">Logout & Account</p>
          <button
            type="submit"
            className="relative overflow-hidden py-2 px-6 bg-themeColor text-white font-popinsSemiBold text-sm mt-5 rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
           active:scale-95 active:shadow-inner focus:outline-none 
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            Logout
            <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
          </button>
        </div>
        {/* Danger Zone */}
        <div className="mt-8 border border-red-200 rounded-lg p-5 bg-red-50">
          <p className="font-popinsSemiBold text-red-600 mb-1">Danger Zone</p>
          <p className="font-popinsRegular text-sm text-gray-500 mb-4">
            Deleting your account is permanent. You will lose access immediately.
          </p>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="py-2 px-6 bg-red-500 text-white font-popinsSemiBold text-sm rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-red-600 hover:scale-105 active:scale-95 focus:outline-none"
          >
            Delete Account
          </button>
        </div>

        {showDeleteModal && (
          <DeleteAccountModal onClose={() => setShowDeleteModal(false)} userEmail={formData.email} />
        )}
      </div>
      <div className="w-full lg:w-[30%]">
        <div className="flex flex-col items-center space-y-3">
          {/* Profile Image */}
          <div className="relative size-48 rounded-full overflow-hidden border-4 border-white shadow-md">
            {isUploadingProfile ? (
              <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
              </div>
            ) : (
              <img
                src={preview}
                alt="Profile"
                className="w-full h-full object-cover rounded-full"
                onError={() => setPreview("/assets/images/user-avatar.png")}
              />
            )}
          </div>

          {/* Hidden Input */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRefProfile}
            onChange={handleImageChange}
            className="hidden"
            disabled={isUploadingProfile}
          />

          {/* Upload Button */}
          <button
            onClick={() => fileInputRefProfile.current?.click()}
            disabled={isUploadingProfile}
            className="bg-gray-100 text-sm font-popinsMedium hover:bg-gray-200 text-gray-800 font-medium py-2 px-5 rounded-md shadow transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
          >
            {isUploadingProfile ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              "Change Profile Picture"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
